import { Dimensions, Platform, useWindowDimensions } from "react-native";
import { useMemo } from "react";

const guidelineBaseWidth = 1440; // Base width where design looks perfect

// Get current width - will be updated on window resize for web
const getCurrentWidth = (): number => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.innerWidth;
  }
  return Dimensions.get("window").width;
};

/**
 * Pure scaling function - scales based on screen width ratio
 * @param size - The base size to scale
 * @returns Scaled size based on current width vs guideline base width
 */
export const scale = (size: number): number => {
  if (Platform.OS !== 'web') {
    return size; // No scaling on native platforms
  }
  const width = getCurrentWidth();
  return (width / guidelineBaseWidth) * size;
};

/**
 * Moderate scaling - less aggressive scaling that feels more natural
 * Uses a factor to blend between original size and fully scaled size
 * @param size - The base size to scale
 * @param factor - Scaling factor (0-1). Default 0.5 means 50% of the difference
 * @returns Moderately scaled size
 */
export const moderateScale = (size: number, factor: number = 0.5): number => {
  if (Platform.OS !== 'web') {
    return size; // No scaling on native platforms
  }
  const scaled = scale(size);
  return size + (scaled - size) * factor;
};

/**
 * Hook version that updates on window resize (for React Native Web)
 * Use this in components that need to react to window size changes
 */
export const useScale = () => {
  const { width: currentWidth } = useWindowDimensions();
  
  const scaleFn = useMemo(() => {
    return (size: number): number => {
      if (Platform.OS !== 'web') {
        return size;
      }
      return (currentWidth / guidelineBaseWidth) * size;
    };
  }, [currentWidth]);

  const moderateScaleFn = useMemo(() => {
    return (size: number, factor: number = 0.5): number => {
      if (Platform.OS !== 'web') {
        return size;
      }
      const scaled = scaleFn(size);
      return size + (scaled - size) * factor;
    };
  }, [scaleFn]);

  return {
    scale: scaleFn,
    moderateScale: moderateScaleFn,
    width: currentWidth,
  };
};

export default {
  scale,
  moderateScale,
  useScale,
};

